<template>
  <div>
    <div class="page-header">
      <h1 class="page-title">Eventos</h1>
      <div class="header-actions">
        <label v-if="arquivadosCount" class="toggle-arquivados">
          <input type="checkbox" v-model="mostrarArquivados" />
          Mostrar arquivados ({{ arquivadosCount }})
        </label>
        <button v-if="authStore.canManageEventos" class="btn-primary" @click="openCreate">+ Novo evento</button>
      </div>
    </div>

    <SemEventoBanner v-if="organizadorSemEventos" :nome="authStore.adminName" />

    <SkeletonLoader v-if="store.loading" variant="row" :count="6" />

    <div v-else-if="eventosVisiveis.length" class="grid">
      <div v-for="e in eventosVisiveis" :key="e.id" class="evento-card card">
        <div class="card-head">
          <strong class="nome">{{ e.nome }}</strong>
          <StatusChip :status="e.status" kind="evento" />
        </div>
        <div class="card-body">
          <p class="link">URL pública: <a :href="`/${e.slug}`" target="_blank">/{{ e.slug }}</a></p>
          <div class="meta">
            <span>{{ e._count?.inscricoes || 0 }} inscrições</span>
            <span>·</span>
            <span>{{ e._count?.planos || 0 }} planos</span>
          </div>
          <div v-if="e.status === 'RASCUNHO'" class="alert alert-warn">
            🔒 Rascunho — não aparece pra inscrição até publicar
          </div>
          <div v-else-if="e.status === 'ABERTO' && (!e._count?.planos)" class="alert alert-danger">
            ⚠️ Aberto mas sem planos. Adicione ao menos 1 plano.
          </div>
          <div v-else-if="e.status === 'ABERTO'" class="alert alert-ok">
            ✓ Recebendo inscrições em /{{ e.slug }}
          </div>
          <div v-else-if="e.status === 'ENCERRADO'" class="alert alert-muted">
            Inscrições encerradas
          </div>
          <div v-else-if="e.status === 'ARQUIVADO'" class="alert alert-muted">
            📦 Arquivado — oculto da lista por padrão
          </div>
        </div>
        <div class="card-actions">
          <button v-if="e.status === 'RASCUNHO'" class="btn-primary btn-sm" @click="publicar(e)" :disabled="!e._count?.planos">
            Publicar
          </button>
          <button class="btn-secondary btn-sm" @click="openEdit(e)">Editar</button>
          <button
            v-if="e.status === 'ARQUIVADO' && authStore.isRoot"
            class="btn-danger btn-sm"
            @click="pedirExclusao(e)"
          >Excluir</button>
        </div>
      </div>
    </div>

    <div v-if="aExcluir" class="modal-overlay" @click.self="cancelarExclusao">
      <div class="modal-card excluir-card">
        <h3 class="modal-title">Excluir “{{ aExcluir.nome }}”?</h3>
        <p class="excluir-txt">
          O evento some do painel junto com seus planos e inscrições. Não há tela
          para desfazer — a recuperação depende de acesso ao banco.
        </p>
        <label class="excluir-label">
          Digite <strong>{{ aExcluir.nome }}</strong> para confirmar:
          <input
            v-model="confirmacao"
            class="input excluir-input"
            type="text"
            autocomplete="off"
            @keyup.enter="nomeConfere && confirmarExclusao()"
          />
        </label>
        <p v-if="erroExclusao" class="excluir-erro">{{ erroExclusao }}</p>
        <div class="excluir-acoes">
          <button class="btn-secondary" :disabled="excluindo" @click="cancelarExclusao">Cancelar</button>
          <button
            class="btn-danger"
            :disabled="!nomeConfere || excluindo"
            @click="confirmarExclusao"
          >{{ excluindo ? 'Excluindo…' : 'Excluir evento' }}</button>
        </div>
      </div>
    </div>

    <EmptyState
      v-else-if="!organizadorSemEventos"
      icon="📅"
      title="Sem eventos ainda"
      :description="authStore.canManageEventos ? 'Cadastre o primeiro evento.' : 'Aguarde um admin criar eventos.'"
      :actionLabel="authStore.canManageEventos ? '+ Novo evento' : null"
      @action="openCreate"
    />

    <EventoForm v-if="showForm" :evento="editing" @save="handleSave" @cancel="closeForm" />
  </div>
</template>

<script>
import { useAuthStore } from '../../stores/auth'
import { useEventosStore } from '../../stores/eventos'
import EventoForm from '../../components/admin/EventoForm.vue'
import StatusChip from '../../components/ui/StatusChip.vue'
import SkeletonLoader from '../../components/ui/SkeletonLoader.vue'
import EmptyState from '../../components/ui/EmptyState.vue'
import SemEventoBanner from '../../components/ui/SemEventoBanner.vue'

export default {
  name: 'EventosPage',
  components: { EventoForm, StatusChip, SkeletonLoader, EmptyState, SemEventoBanner },
  data() {
    return {
      showForm: false, editing: null, mostrarArquivados: false,
      aExcluir: null, confirmacao: '', excluindo: false, erroExclusao: ''
    }
  },
  computed: {
    authStore() { return useAuthStore() },
    /**
     * A digitacao do nome e o atrito de proposito: a exclusao nao tem desfazer
     * pela tela, entao confirmar sem ler o nome nao deve ser possivel.
     */
    nomeConfere() {
      return !!this.aExcluir && this.confirmacao.trim() === this.aExcluir.nome
    },
    store() { return useEventosStore() },
    // Os testes E2E deixam eventos arquivados pra tras (o DELETE da API so arquiva),
    // entao a lista esconde ARQUIVADO por padrao.
    eventosVisiveis() {
      if (this.mostrarArquivados) return this.store.eventos
      return this.store.eventos.filter(e => e.status !== 'ARQUIVADO')
    },
    arquivadosCount() {
      return this.store.eventos.filter(e => e.status === 'ARQUIVADO').length
    },
    organizadorSemEventos() {
      return this.authStore.isOrganizador && !this.store.loading && this.eventosVisiveis.length === 0
    }
  },
  async mounted() { await this.store.fetchAll() },
  methods: {
    pedirExclusao(evento) {
      this.aExcluir = evento
      this.confirmacao = ''
      this.erroExclusao = ''
    },
    cancelarExclusao() {
      if (this.excluindo) return
      this.aExcluir = null
      this.confirmacao = ''
      this.erroExclusao = ''
    },
    async confirmarExclusao() {
      if (!this.nomeConfere) return
      this.excluindo = true
      this.erroExclusao = ''
      try {
        const res = await this.store.deletar(this.aExcluir.id)
        this.$toast.open({ message: res?.message || 'Evento excluído', type: 'success', position: 'top-right' })
        this.aExcluir = null
        this.confirmacao = ''
      } catch (err) {
        // 409 traz o motivo pronto do servidor (evento com dinheiro
        // movimentado, ou nao arquivado). Exibe como veio.
        this.erroExclusao = err.response?.data?.message || 'Não foi possível excluir.'
      } finally {
        this.excluindo = false
      }
    },
    openCreate() {
      if (!this.authStore.canManageEventos) return
      this.editing = null
      this.showForm = true
    },
    async openEdit(evento) {
      try {
        this.editing = await this.store.getOne(evento.id)
        this.showForm = true
      } catch (err) {
        this.$toast.open({ message: 'Falha ao carregar evento', type: 'error', position: 'top-right' })
      }
    },
    closeForm() {
      this.showForm = false
      this.editing = null
    },
    async publicar(e) {
      if (!e._count?.planos) {
        this.$toast.open({ message: 'Adicione ao menos 1 plano antes de publicar', type: 'warning', position: 'top-right' })
        return
      }
      try {
        await this.store.update(e.id, { status: 'ABERTO' })
        this.$toast.open({ message: `Evento publicado em /${e.slug}`, type: 'success', position: 'top-right' })
      } catch (err) {
        this.$toast.open({ message: err.response?.data?.message || 'Erro ao publicar', type: 'error', position: 'top-right' })
      }
    },
    async handleSave(payload) {
      try {
        if (this.editing) {
          await this.store.update(this.editing.id, payload)
          this.$toast.open({ message: 'Evento atualizado', type: 'success', position: 'top-right' })
        } else {
          await this.store.create(payload)
          this.$toast.open({ message: 'Evento criado', type: 'success', position: 'top-right' })
        }
        this.closeForm()
      } catch (err) {
        const data = err.response?.data
        let msg = data?.message || 'Erro ao salvar'
        if (Array.isArray(data?.errors) && data.errors.length) {
          msg += ': ' + data.errors.map(e => `${e.field} - ${e.message}`).join('; ')
        }
        this.$toast.open({ message: msg, type: 'error', position: 'top-right', duration: 8000 })
      }
    }
  }
}
</script>

<style scoped>
.excluir-card { max-width: 460px; }
.excluir-txt {
  margin: 0 0 var(--spacing-base);
  font-size: var(--text-sm);
  line-height: 1.5;
  color: var(--color-text-muted);
}
.excluir-label {
  display: block;
  font-size: var(--text-sm);
  color: var(--color-text-primary);
}
.excluir-input { width: 100%; margin-top: var(--spacing-xs); }
.excluir-erro {
  margin: var(--spacing-sm) 0 0;
  font-size: var(--text-sm);
  line-height: 1.5;
  color: var(--color-danger);
}
.excluir-acoes {
  display: flex;
  gap: var(--spacing-sm);
  justify-content: flex-end;
  margin-top: var(--spacing-lg);
  flex-wrap: wrap;
}
@media (max-width: 480px) {
  .excluir-acoes { flex-direction: column-reverse; }
  .excluir-acoes button { width: 100%; }
}
.page-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: var(--spacing-lg); }
.page-header .page-title { margin-bottom: 0; }
.header-actions { display: flex; align-items: center; gap: var(--spacing-base); }
.toggle-arquivados {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: var(--text-xs);
  color: var(--color-text-muted);
  cursor: pointer;
  user-select: none;
}
.toggle-arquivados input { cursor: pointer; }
.grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: var(--spacing-base);
}
.evento-card { padding: var(--spacing-base); display: flex; flex-direction: column; gap: var(--spacing-sm); }
.card-head { display: flex; align-items: center; justify-content: space-between; gap: var(--spacing-sm); }
.nome { font-size: var(--text-md); }
.card-body { font-size: var(--text-sm); color: var(--color-text-secondary); }
.link a { color: var(--color-accent); text-decoration: none; }
.link a:hover { text-decoration: underline; }
.meta { color: var(--color-text-muted); font-size: var(--text-xs); display: flex; gap: 6px; margin-top: 4px; }
.card-actions { display: flex; gap: var(--spacing-sm); }
.btn-sm { padding: 6px 12px; font-size: var(--text-xs); }
.alert {
  margin-top: var(--spacing-sm);
  padding: 6px 8px;
  border-radius: var(--radius-sm);
  font-size: var(--text-xs);
  line-height: 1.4;
}
.alert-warn { background: #fef3c7; color: #92400e; }
.alert-danger { background: #fee2e2; color: #b91c1c; }
.alert-ok { background: #dcfce7; color: #166534; }
.alert-muted { background: var(--color-bg-hover); color: var(--color-text-muted); }
</style>
